import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../utils/firebase/Firebase.utils";
import UserScreenWindow from "../Components/UserScreenWindow";
import IOSSpinner from "../../../components/IOSSpinner";
import { unitNumberLabel } from "../../../utils/constants";

export default function ParcelUserDetailspage() {
  const { parcelId } = useParams();
  const navigate = useNavigate();
  const [parcel, setParcel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchParcelDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const parcelRef = doc(db, "parcels", parcelId);
        const parcelSnap = await getDoc(parcelRef);

        if (parcelSnap.exists()) {
          setParcel({ id: parcelSnap.id, ...parcelSnap.data() });
        } else {
          setError("Parcel not found");
        }
      } catch (err) {
        console.error("Error fetching parcel details:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (parcelId) {
      fetchParcelDetails();
    }
  }, [parcelId]);

  const handleBack = () => {
    navigate("/user/parcels");
  };

  if (loading) {
    return (
      <UserScreenWindow>
        <div className="flex justify-center items-center h-64">
          <IOSSpinner />
        </div>
      </UserScreenWindow>
    );
  }

  if (error) {
    return (
      <UserScreenWindow>
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-[#363062] text-white rounded hover:bg-opacity-90 transition-colors"
          >
            Back to Parcels
          </button>
        </div>
      </UserScreenWindow>
    );
  }

  if (!parcel) {
    return (
      <UserScreenWindow>
        <div
          className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Not Found: </strong>
          <span className="block sm:inline">
            The requested parcel could not be found.
          </span>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-[#363062] text-white rounded hover:bg-opacity-90 transition-colors"
          >
            Back to Parcels
          </button>
        </div>
      </UserScreenWindow>
    );
  }

  return (
    <UserScreenWindow label={"Parcel Details"}>
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        <div className="md:flex">
          <div className="md:w-1/3 bg-gray-100 p-6 flex items-center justify-center">
            {parcel.imageUrl ? (
              <img
                src={parcel.imageUrl}
                alt={`Parcel ${parcel.parcelNumber}`}
                className="max-h-64 object-contain"
              />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-32 w-32 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            )}
          </div>

          <div className="md:w-2/3 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
              <h1 className="text-xl font-semibold text-[#363062]">
                Parcel #{parcel.parcelNumber}
              </h1>
              <span
                className={`px-3 py-1 text-sm font-medium rounded-full mt-2 md:mt-0 ${
                  parcel.status === "Stored"
                    ? "bg-green-100 text-green-800"
                    : parcel.status === "Delivered"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {parcel.status}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  {unitNumberLabel}
                </h3>
                <p className="text-base text-gray-800">{parcel.unitNumber}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Recipient
                </h3>
                <p className="text-base text-gray-800">
                  {parcel.recipientName}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Delivery Company
                </h3>
                <p className="text-base text-gray-800">
                  {parcel.deliveryCompany}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Storage Location
                </h3>
                <p className="text-base text-gray-800">
                  {parcel.storageLocation || "Not specified"}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Date Received
                </h3>
                <p className="text-base text-gray-800">
                  {new Date(parcel.dateTime).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }) || "Not specified"}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-base font-medium text-[#363062] mb-2">
                Additional Information
              </h3>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  This parcel was registered by {parcel.createdBy} on{" "}
                  {new Date(parcel.createdAt).toLocaleDateString()}.
                  {parcel.status === "Stored" &&
                    " It is currently being held for you at the storage location."}
                  {parcel.status === "Delivered" &&
                    " It has been delivered to your unit."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserScreenWindow>
  );
}
